/**
 * SidebarDesktop Component
 *
 * Main desktop sidebar container with:
 * - Fixed position on left side
 * - Collapsible functionality
 * - Full height with proper scrolling
 * - Dark mode support
 * - Logo/brand area
 * - Search bar
 * - Navigation items
 * - Theme toggle
 *
 * @module SidebarDesktop
 */

import React from 'react';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useSidebar } from '../hooks/useSidebar';
import SearchBar from './SearchBar';
import NavigationItem from './NavigationItem';
import NavigationItemIcon from './NavigationItemIcon';
import NavigationUtilityMenu from './NavigationUtilityMenu';
import ThemeToggle from './ThemeToggle';
import ThemeToggleIcon from './ThemeToggleIcon';

/**
 * SidebarDesktop Component
 *
 * Desktop-only sidebar with full navigation functionality and collapse support
 */
const SidebarDesktop = () => {
  const {
    navigationAreas,
    navigationUtilities,
    activeNavigationArea,
    activeMenu,
    handleMenuClick,
    isCollapsed,
    toggleCollapse
  } = useSidebar();

  const width = isCollapsed ? 64 : 240;

  return (
    <aside
      className="ui-motion-resize fixed left-0 top-0 h-screen flex flex-col z-30"
      style={{
        width: `${width}px`,
        background: 'var(--surface-1)',
        borderRight: '1px solid var(--glass-border)'
      }}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Logo/Brand Area with Collapse Button */}
      <div className="p-4 border-b border-glass-border flex items-center justify-between">
        {isCollapsed ? (
          <div className="w-full flex flex-col items-center gap-2">
            <div className="w-9 h-9 bg-accent flex items-center justify-center">
              <span className="text-accent-foreground font-black text-lg font-display">T</span>
            </div>
            <button
              onClick={toggleCollapse}
              className="p-1 hover:bg-surface-3"
              aria-label="Expand sidebar"
            >
              <IconChevronRight size={16} className="text-text-tertiary" />
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-accent flex items-center justify-center">
                <span className="text-accent-foreground font-black text-lg font-display">T</span>
              </div>
              <div>
                <h1 className="font-display uppercase font-black text-base text-text-primary tracking-tight">
                  TAKEOVER
                </h1>
                <p className="text-[10px] uppercase tracking-widest text-text-tertiary font-medium">
                  Creator Platform
                </p>
              </div>
            </div>
            <button
              onClick={toggleCollapse}
              className="p-1.5 hover:bg-surface-3"
              aria-label="Collapse sidebar"
            >
              <IconChevronLeft size={18} className="text-text-tertiary" />
            </button>
          </>
        )}
      </div>

      {/* Search Bar - hidden when collapsed */}
      {!isCollapsed && (
        <div className="px-4 py-3 border-b border-glass-border">
          <SearchBar />
        </div>
      )}

      {/* Navigation Items */}
      <nav
        className="flex-1 overflow-y-auto px-3 py-3 space-y-1 scrollbar-glass"
        aria-label="Primary navigation"
      >
        {!isCollapsed && (
          <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
            Areas
          </p>
        )}
        {navigationAreas.length === 0 ? (
          !isCollapsed && (
            <div className="text-center py-8">
              <p className="text-sm text-text-tertiary">
                No menu items found
              </p>
            </div>
          )
        ) : (
          navigationAreas.map((area) => (
            isCollapsed ? (
              <NavigationItemIcon
                key={area.id}
                item={area}
                isActive={activeNavigationArea?.id === area.id}
                onClick={() => handleMenuClick(area.id, area.path)}
              />
            ) : (
              <NavigationItem
                key={area.id}
                item={area}
                isActive={activeNavigationArea?.id === area.id}
                onClick={() => handleMenuClick(area.id, area.path)}
              />
            )
          ))
        )}
      </nav>

      <div className="px-3 py-2 border-t border-glass-border">
        <NavigationUtilityMenu
          items={navigationUtilities}
          activeMenu={activeMenu}
          isCollapsed={isCollapsed}
          onMenuClick={handleMenuClick}
        />
      </div>

      <div className="p-3 border-t border-glass-border">
        {isCollapsed ? <ThemeToggleIcon /> : <ThemeToggle />}
      </div>
    </aside>
  );
};

SidebarDesktop.propTypes = {};

export default React.memo(SidebarDesktop);
